import { useLocation } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';

import { Icons } from '@/assets/icons/icons';
import { IconButton } from '@/components/ui';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import { ChatPanelContent } from './context-chat-panel';
import { sidebar as sidebarConfig } from './context-chat.config';
import { useDockMetrics } from './context-chat.hooks';
import { contextChatStore } from './context-chat.state';
import { shouldShowOnPath, surfaceForSession } from './context-chat.utils';

export const ContextChatSidebar = observer(function ContextChatSidebar() {
  const { pathname } = useLocation();
  const session = contextChatStore.activeSession;
  const { mode, preferDocked, dockOnly } = contextChatStore;
  const routeHidden = !shouldShowOnPath(pathname) || contextChatStore.embedded;
  const docked = mode === 'docked' && !!session;
  const surface = surfaceForSession(session);

  const { canDock, maxWidth, width } = useDockMetrics();

  useEffect(() => {
    contextChatStore.setCanDock(canDock);
  }, [canDock]);

  useEffect(() => {
    if (routeHidden) return;
    if (docked && !canDock) {
      contextChatStore.float();
      trackEvent('CHAT_UNDOCKED', {
        context: surface,
        reason: 'auto_narrow',
      });
    } else if (mode === 'floating' && (preferDocked || dockOnly) && canDock) {
      contextChatStore.dock();
    }
  }, [canDock, dockOnly, docked, mode, preferDocked, routeHidden, surface]);

  useEffect(() => {
    if (!docked || routeHidden) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      contextChatStore.collapse();
    };

    window.addEventListener('keydown', handler);

    return () => window.removeEventListener('keydown', handler);
  }, [docked, routeHidden]);

  if (!docked || routeHidden || !canDock) return null;

  return (
    <aside
      className="relative shrink-0 mr-6 mb-4 overflow-hidden rounded-2xl border border-border bg-surface"
      style={{ width }}
    >
      <div className="flex h-full flex-col" style={{ width }}>
        <SidebarResizeHandle maxWidth={maxWidth} />
        <DockedTopBar />
        <div className="min-h-0 flex-1">
          <ChatPanelContent />
        </div>
      </div>
    </aside>
  );
});

const DockedTopBar = observer(function DockedTopBar() {
  const session = contextChatStore.activeSession;
  const isGeneral = session?.context.type === 'general';

  return (
    <div className="flex shrink-0 items-center justify-end gap-1 px-2 pt-2">
      {isGeneral && (
        <IconButton
          size="sm"
          variant="ghost"
          icon={Icons.plus}
          tooltip="New chat"
          onClick={() => {
            void contextChatStore.startNewGeneralChat();
          }}
        />
      )}
      {!contextChatStore.dockOnly && (
        <IconButton
          size="sm"
          variant="ghost"
          icon={Icons.minimize}
          tooltip="Undock"
          onClick={() => {
            contextChatStore.undock();
            trackEvent('CHAT_UNDOCKED', {
              context: surfaceForSession(session),
              reason: 'user',
            });
          }}
        />
      )}
      <IconButton
        size="sm"
        variant="ghost"
        icon={Icons.cancel}
        tooltip="Close"
        onClick={() => contextChatStore.collapse()}
      />
    </div>
  );
});

const SidebarResizeHandle = observer(function SidebarResizeHandle({
  maxWidth = sidebarConfig.maxWidth,
}: {
  maxWidth?: number;
}) {
  const dragRef = useRef<{ startX: number; startWidth: number } | null>(null);

  // Failsafe: if the handle unmounts mid-drag (e.g. sidebar closes), put
  // the body styles back so the user isn't stuck with col-resize cursor.
  useEffect(() => {
    return () => {
      if (!dragRef.current) return;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      contextChatStore.setResizingSidebar(false);
    };
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      startX: e.clientX,
      startWidth: contextChatStore.sidebarWidth,
    };
    contextChatStore.setResizingSidebar(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    const { startX, startWidth } = dragRef.current;
    const delta = startX - e.clientX;
    const next = Math.max(
      sidebarConfig.minWidth,
      Math.min(maxWidth, startWidth + delta)
    );
    contextChatStore.setSidebarWidth(next);
  };

  const endDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    dragRef.current = null;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    contextChatStore.setResizingSidebar(false);
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      data-resize-handle
      data-dragging={contextChatStore.isResizingSidebar ? 'true' : 'false'}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      className="group absolute left-0.5 inset-y-4 w-[6px] flex justify-start cursor-ew-resize z-20"
    >
      <div
        className={cn(
          'w-0 h-full rounded-full bg-border-strongest',
          'group-hover:w-1 group-hover:opacity-100',
          'group-data-[dragging=true]:w-1 group-data-[dragging=true]:opacity-100',
          'transition-[opacity,width] will-change-[width,opacity]'
        )}
      />
    </div>
  );
});
